import { h } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

import Reaction from "../../../foundation/model/Reaction";
import AddReactionButton from "../AddReactionButton";
import Tooltip from "../Tooltip";

import ReactionButton from "./ReactionButton";
import ReactionListLocation from "./ReactionListLocation";
import RemainingReactionsButton from "./RemainingReactionsButton";

import styles from "./ReactionList.module.css";

const REACTION_BUTTON_WIDTH = 44;
const REMAINING_REACTIONS_BUTTON_WIDTH = 30;
const ADD_REACTION_BUTTON_WIDTH = 24;
const REACTION_GAP = 8;

interface Props {
    cmid: string;
    reactions?: Reaction[];
    userId: string;
    location?: ReactionListLocation;
    readOnly?: boolean;
    disabled?: boolean;
    onAddReaction?(cmid: string, reaction: string): void;
    onRemoveReaction?(cmid: string, reaction: string): void;
}

export default function ReactionList({
    cmid,
    reactions,
    userId,
    location = ReactionListLocation.DotPopup,
    readOnly = false,
    disabled = false,
    onAddReaction,
    onRemoveReaction
}: Props) {
    const reactionListRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver>(null);
    const isAddReactionButtonVisible = useMemo(() => readOnly === false && disabled === false, [readOnly, disabled]);
    const [visibleReactionCount, setVisibleReactionCount] = useState(0);

    function getVisibleReactionCount(): number {
        if (reactions === undefined || reactionListRef.current === null) {
            return 0;
        }

        let availableWidth = reactionListRef.current.offsetWidth;
        if (isAddReactionButtonVisible) {
            availableWidth -= ADD_REACTION_BUTTON_WIDTH + REACTION_GAP;
        }

        if (reactions.length * REACTION_BUTTON_WIDTH + (reactions.length - 1) * REACTION_GAP > availableWidth) {
            availableWidth -= REMAINING_REACTIONS_BUTTON_WIDTH + REACTION_GAP;
        }

        return Math.floor((availableWidth + REACTION_GAP) / (REACTION_BUTTON_WIDTH + REACTION_GAP));
    }

    useEffect(() => {
        setVisibleReactionCount(getVisibleReactionCount());

        if (reactionListRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                setVisibleReactionCount(getVisibleReactionCount());
            });

            resizeObserverRef.current.observe(reactionListRef.current);
        } else {
            resizeObserverRef.current?.disconnect();
        }

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, [reactions?.length, readOnly, disabled]);

    function handleSelectReaction(emoji: string) {
        if (readOnly) {
            return;
        }

        const existingReaction = reactions?.find(({ type }) => type === emoji);
        if (existingReaction === undefined || !existingReaction.users.some(({ _id }) => _id === userId)) {
            onAddReaction?.(cmid, emoji);
        } else {
            onRemoveReaction?.(cmid, emoji);
        }
    }

    if (reactions === undefined || reactions.length === 0) {
        return null;
    }

    const visibleReactions = reactions.slice(0, visibleReactionCount);
    const remainingReactions = reactions.slice(visibleReactionCount);

    return (
        <div
            ref={reactionListRef}
            class={styles.reactionList}>
            {visibleReactions.map(({ _id, type, users }) => (
                <ReactionButton
                    key={_id}
                    emoji={type}
                    users={users}
                    userId={userId}
                    location={location}
                    readOnly={readOnly}
                    disabled={disabled}
                    onClick={handleSelectReaction}/>
            ))}
            {remainingReactions.length !== 0 && (
                <RemainingReactionsButton
                    reactions={remainingReactions}
                    userId={userId}
                    location={location}
                    readOnly={readOnly}
                    disabled={disabled}
                    onSelect={handleSelectReaction}/>
            )}
            {isAddReactionButtonVisible && (
                <Tooltip
                    content="Add reaction"
                    hideDelay={50}>
                    <AddReactionButton
                        onSelectReaction={handleSelectReaction}/>
                </Tooltip>
            )}
        </div>
    );
}
